import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth, useTheme, useTenant } from '../../context/AppContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { Sun, Moon, ArrowLeft, Save, User, Lock, Palette, Star, Share2, Upload, Loader2, Camera } from 'lucide-react';
import SenArtisteLogo from '../../components/SenArtisteLogo';
import { AIWriter } from '../../components/ai/AIWriter';
import { FaFacebook, FaInstagram, FaTiktok, FaYoutube, FaXTwitter } from 'react-icons/fa6';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Helper pour résoudre les URLs d'images (relatives ou absolues)
const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/api/')) return `${API_BASE}${url}`;
  if (url.startsWith('/')) return `${API_BASE}${url}`;
  return url;
};

// Placeholder images
const PLACEHOLDER_PROFILE = '/images/artist-placeholder.jpg';
const PLACEHOLDER_COVER = 'https://images.unsplash.com/photo-1501281668745-f7f57925c3b4?w=1200&q=80';

const Account = () => {
  const { artistSlug } = useParams();
  const navigate = useNavigate();
  const { user, api, updateProfile } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { tenant, fetchTenant } = useTenant();
  
  const [profileForm, setProfileForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
  });
  
  const [artistForm, setArtistForm] = useState({
    bio: '',
    emailContact: '',
    telephoneContact: '',
    primaryColor: '#D4AF37',
    secondaryColor: '#1A1A1A',
    profilePhotoUrl: '',
    coverPhotoUrl: '',
    numeroOrangeMoney: '',
    numeroWave: '',
  });
  
  const [socialLinksForm, setSocialLinksForm] = useState({
    facebook: '',
    instagram: '',
    tiktok: '',
    youtube: '',
    x: '',
  });
  
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  
  const [saving, setSaving] = useState(false);
  const [uploadingProfile, setUploadingProfile] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const profilePhotoRef = useRef(null);
  const coverPhotoRef = useRef(null);

  // Upload handler for profile/cover photos
  const handlePhotoUpload = async (file, type) => {
    if (!file) return;
    
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error('Image trop volumineuse (max 10MB)');
      return;
    }
    
    const setUploading = type === 'profile' ? setUploadingProfile : setUploadingCover;
    setUploading(true);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await api.post('/upload/file', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (type === 'profile') {
        setArtistForm(p => ({ ...p, profilePhotoUrl: response.data.url }));
      } else {
        setArtistForm(p => ({ ...p, coverPhotoUrl: response.data.url }));
      }
      toast.success('Photo uploadée');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    if (!user || !['ARTISTE', 'MANAGER'].includes(user.role)) {
      navigate(`/${artistSlug}/login`);
      return;
    }
    
    setProfileForm({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      phone: user.phone || '',
    });
    
    fetchTenant(artistSlug);
  }, [user, artistSlug]);

  useEffect(() => {
    if (tenant?.artist) {
      setArtistForm({
        bio: tenant.artist.bio || '',
        emailContact: tenant.artist.emailContact || '',
        telephoneContact: tenant.artist.telephoneContact || '',
        primaryColor: tenant.artist.primaryColor || '#D4AF37',
        secondaryColor: tenant.artist.secondaryColor || '#1A1A1A',
        profilePhotoUrl: tenant.artist.profilePhotoUrl || '',
        coverPhotoUrl: tenant.artist.coverPhotoUrl || '',
        numeroOrangeMoney: tenant.artist.numeroOrangeMoney || '',
        numeroWave: tenant.artist.numeroWave || '',
      });
      
      // Charger les liens sociaux existants
      const socialLinks = tenant.artist.socialLinks || [];
      const socialData = {
        facebook: '',
        instagram: '',
        tiktok: '',
        youtube: '',
        x: '',
      };
      socialLinks.forEach(link => {
        const platform = link.platform?.toLowerCase();
        if (platform === 'facebook') socialData.facebook = link.url || '';
        if (platform === 'instagram') socialData.instagram = link.url || '';
        if (platform === 'tiktok') socialData.tiktok = link.url || '';
        if (platform === 'youtube') socialData.youtube = link.url || '';
        if (platform === 'x' || platform === 'twitter') socialData.x = link.url || '';
      });
      setSocialLinksForm(socialData);
    }
  }, [tenant]);

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await updateProfile(profileForm);
      toast.success('Profil mis à jour');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveArtist = async () => {
    if (!user?.tenantId) return;
    setSaving(true);
    try {
      await api.put(`/artists/tenant/${user.tenantId}`, artistForm);
      toast.success('Paramètres artiste mis à jour');
      fetchTenant(artistSlug);
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSocialLinks = async () => {
    if (!user?.tenantId) return;
    setSaving(true);
    try {
      const socialLinks = [];
      if (socialLinksForm.facebook) {
        socialLinks.push({ platform: 'FACEBOOK', url: socialLinksForm.facebook, iconKey: 'facebook' });
      }
      if (socialLinksForm.instagram) {
        socialLinks.push({ platform: 'INSTAGRAM', url: socialLinksForm.instagram, iconKey: 'instagram' });
      }
      if (socialLinksForm.tiktok) {
        socialLinks.push({ platform: 'TIKTOK', url: socialLinksForm.tiktok, iconKey: 'tiktok' });
      }
      if (socialLinksForm.youtube) {
        socialLinks.push({ platform: 'YOUTUBE', url: socialLinksForm.youtube, iconKey: 'youtube' });
      }
      if (socialLinksForm.x) {
        socialLinks.push({ platform: 'X', url: socialLinksForm.x, iconKey: 'x' });
      }
      
      await api.put(`/artists/tenant/${user.tenantId}/social-links`, { socialLinks });
      toast.success('Réseaux sociaux mis à jour');
      fetchTenant(artistSlug);
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    
    if (passwordForm.newPassword.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    setSaving(true);
    try {
      await api.put('/auth/change-password', {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });
      toast.success('Mot de passe modifié');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du changement de mot de passe');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-border px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(`/${artistSlug}/management`)} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
          <div className="flex items-center gap-2">
            <SenArtisteLogo className="w-6 h-6" />
            <span className="font-syne font-bold">Mon Compte</span>
          </div>
        </div>
        
        <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle">
          {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </Button>
      </header>

      <div className="max-w-4xl mx-auto p-4 md:p-8 space-y-8">
        {/* Profile Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                Informations personnelles
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Prénom</Label>
                  <Input 
                    value={profileForm.firstName} 
                    onChange={(e) => setProfileForm(p => ({ ...p, firstName: e.target.value }))}
                    data-testid="profile-firstName"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nom</Label>
                  <Input 
                    value={profileForm.lastName} 
                    onChange={(e) => setProfileForm(p => ({ ...p, lastName: e.target.value }))}
                    data-testid="profile-lastName"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Téléphone</Label>
                <Input 
                  value={profileForm.phone} 
                  onChange={(e) => setProfileForm(p => ({ ...p, phone: e.target.value }))}
                  data-testid="profile-phone"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input value={user?.email || ''} disabled className="bg-muted" />
              </div>
              <Button onClick={handleSaveProfile} disabled={saving} className="btn-primary" data-testid="save-profile">
                <Save className="w-4 h-4 mr-2" />
                Enregistrer
              </Button>
            </CardContent>
          </Card>
        </motion.div>

        {/* Artist Settings - Only for ARTISTE role */}
        {user?.role === 'ARTISTE' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Palette className="w-5 h-5 text-primary" />
                  Paramètres du profil artiste
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Biographie</Label>
                  <Textarea 
                    value={artistForm.bio} 
                    onChange={(e) => setArtistForm(p => ({ ...p, bio: e.target.value }))}
                    rows={4}
                    data-testid="artist-bio"
                  />
                  <AIWriter
                    api={api}
                    field="bio"
                    value={artistForm.bio}
                    onApply={(text) => setArtistForm(p => ({ ...p, bio: text }))}
                    context={{
                      nomDeScene: tenant?.artist?.nomDeScene,
                      prenom: tenant?.artist?.prenom,
                      nom: tenant?.artist?.nom,
                      genreMusical: tenant?.artist?.genreMusical,
                    }}
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Email de contact</Label>
                    <Input 
                      type="email"
                      value={artistForm.emailContact} 
                      onChange={(e) => setArtistForm(p => ({ ...p, emailContact: e.target.value }))}
                      data-testid="artist-emailContact"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Téléphone de contact</Label>
                    <Input 
                      value={artistForm.telephoneContact} 
                      onChange={(e) => setArtistForm(p => ({ ...p, telephoneContact: e.target.value }))}
                      data-testid="artist-telephoneContact"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Couleur principale</Label>
                    <div className="flex gap-2">
                      <Input 
                        type="color"
                        value={artistForm.primaryColor} 
                        onChange={(e) => setArtistForm(p => ({ ...p, primaryColor: e.target.value }))}
                        className="w-16 h-10 p-1"
                        data-testid="artist-primaryColor"
                      />
                      <Input 
                        value={artistForm.primaryColor} 
                        onChange={(e) => setArtistForm(p => ({ ...p, primaryColor: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Couleur secondaire</Label>
                    <div className="flex gap-2">
                      <Input 
                        type="color"
                        value={artistForm.secondaryColor} 
                        onChange={(e) => setArtistForm(p => ({ ...p, secondaryColor: e.target.value }))}
                        className="w-16 h-10 p-1"
                        data-testid="artist-secondaryColor"
                      />
                      <Input 
                        value={artistForm.secondaryColor} 
                        onChange={(e) => setArtistForm(p => ({ ...p, secondaryColor: e.target.value }))}
                      />
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Photo de profil</Label>
                  <div className="flex items-center gap-4">
                    <div className="w-24 h-24 rounded-full border-2 border-dashed border-primary/30 overflow-hidden bg-muted flex items-center justify-center">
                      {artistForm.profilePhotoUrl ? (
                        <img 
                          src={resolveImageUrl(artistForm.profilePhotoUrl)} 
                          alt="Profile" 
                          className="w-full h-full object-cover" 
                          onError={(e) => { if (e.target.src !== PLACEHOLDER_PROFILE) e.target.src = PLACEHOLDER_PROFILE; }}
                        />
                      ) : (
                        <User className="w-8 h-8 text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex-1">
                      <input
                        type="file"
                        ref={profilePhotoRef}
                        className="hidden"
                        accept="image/*"
                        onChange={(e) => handlePhotoUpload(e.target.files[0], 'profile')}
                      />
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => profilePhotoRef.current?.click()}
                        disabled={uploadingProfile}
                        data-testid="upload-profile-photo"
                      >
                        {uploadingProfile ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Upload...</>
                        ) : (
                          <><Camera className="w-4 h-4 mr-2" /> Changer la photo</>
                        )}
                      </Button>
                      <p className="text-xs text-muted-foreground mt-1">JPG, PNG, WEBP (max 10MB)</p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Photo de couverture</Label>
                  <div className="relative w-full h-32 rounded-lg border-2 border-dashed border-primary/30 overflow-hidden bg-muted">
                    {artistForm.coverPhotoUrl ? (
                      <img 
                        src={resolveImageUrl(artistForm.coverPhotoUrl)} 
                        alt="Cover" 
                        className="w-full h-full object-cover" 
                        onError={(e) => { if (e.target.src !== PLACEHOLDER_COVER) e.target.src = PLACEHOLDER_COVER; }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Upload className="w-8 h-8 text-muted-foreground" />
                      </div>
                    )}
                    <input
                      type="file"
                      ref={coverPhotoRef}
                      className="hidden"
                      accept="image/*"
                      onChange={(e) => handlePhotoUpload(e.target.files[0], 'cover')}
                    />
                    <Button 
                      type="button" 
                      variant="secondary" 
                      size="sm"
                      className="absolute bottom-2 right-2"
                      onClick={() => coverPhotoRef.current?.click()}
                      disabled={uploadingCover}
                      data-testid="upload-cover-photo"
                    >
                      {uploadingCover ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Upload...</>
                      ) : (
                        <><Camera className="w-4 h-4 mr-2" /> Changer</>
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">Format recommandé: 1920x400px</p>
                </div>
                
                <div className="border-t border-border pt-4 mt-4">
                  <h4 className="font-medium mb-4">Informations de paiement</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Numéro Orange Money</Label>
                      <Input 
                        value={artistForm.numeroOrangeMoney} 
                        onChange={(e) => setArtistForm(p => ({ ...p, numeroOrangeMoney: e.target.value }))}
                        placeholder="+221 77 xxx xx xx"
                        data-testid="artist-orangeMoney"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Numéro Wave</Label>
                      <Input 
                        value={artistForm.numeroWave} 
                        onChange={(e) => setArtistForm(p => ({ ...p, numeroWave: e.target.value }))}
                        placeholder="+221 76 xxx xx xx"
                        data-testid="artist-wave"
                      />
                    </div>
                  </div>
                </div>
                
                <Button onClick={handleSaveArtist} disabled={saving} className="btn-primary" data-testid="save-artist">
                  <Save className="w-4 h-4 mr-2" />
                  Enregistrer les paramètres
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Social Links Section */}
        {user?.role === 'ARTISTE' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
          >
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Share2 className="w-5 h-5 text-primary" />
                  Réseaux sociaux
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground mb-4">
                  Ajoutez vos liens pour que vos fans puissent vous suivre sur les réseaux sociaux.
                </p>
                
                <div className="space-y-4">
                  {/* Facebook */}
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#1877F2] flex items-center justify-center flex-shrink-0">
                      <FaFacebook className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <Label className="text-xs text-muted-foreground">Facebook</Label>
                      <Input 
                        value={socialLinksForm.facebook} 
                        onChange={(e) => setSocialLinksForm(p => ({ ...p, facebook: e.target.value }))}
                        placeholder="https://facebook.com/votre-page"
                        data-testid="social-facebook"
                      />
                    </div>
                  </div>
                  
                  {/* Instagram */}
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#833AB4] via-[#FD1D1D] to-[#F77737] flex items-center justify-center flex-shrink-0">
                      <FaInstagram className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <Label className="text-xs text-muted-foreground">Instagram</Label>
                      <Input 
                        value={socialLinksForm.instagram} 
                        onChange={(e) => setSocialLinksForm(p => ({ ...p, instagram: e.target.value }))}
                        placeholder="https://instagram.com/votre-compte"
                        data-testid="social-instagram"
                      />
                    </div>
                  </div>
                  
                  {/* TikTok */}
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-black flex items-center justify-center flex-shrink-0">
                      <FaTiktok className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <Label className="text-xs text-muted-foreground">TikTok</Label>
                      <Input 
                        value={socialLinksForm.tiktok} 
                        onChange={(e) => setSocialLinksForm(p => ({ ...p, tiktok: e.target.value }))}
                        placeholder="https://tiktok.com/@votre-compte"
                        data-testid="social-tiktok"
                      />
                    </div>
                  </div>
                  
                  {/* YouTube */}
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#FF0000] flex items-center justify-center flex-shrink-0">
                      <FaYoutube className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <Label className="text-xs text-muted-foreground">YouTube</Label>
                      <Input 
                        value={socialLinksForm.youtube} 
                        onChange={(e) => setSocialLinksForm(p => ({ ...p, youtube: e.target.value }))}
                        placeholder="https://youtube.com/@votre-chaine"
                        data-testid="social-youtube"
                      />
                    </div>
                  </div>
                  
                  {/* X (Twitter) */}
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-black flex items-center justify-center flex-shrink-0">
                      <FaXTwitter className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <Label className="text-xs text-muted-foreground">X (Twitter)</Label>
                      <Input 
                        value={socialLinksForm.x} 
                        onChange={(e) => setSocialLinksForm(p => ({ ...p, x: e.target.value }))}
                        placeholder="https://x.com/votre-compte"
                        data-testid="social-x"
                      />
                    </div>
                  </div>
                </div>
                
                <Button onClick={handleSaveSocialLinks} disabled={saving} className="btn-primary" data-testid="save-social">
                  <Save className="w-4 h-4 mr-2" />
                  Enregistrer les réseaux sociaux
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Password Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="w-5 h-5 text-primary" />
                Changer le mot de passe
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div className="space-y-2">
                  <Label>Mot de passe actuel</Label>
                  <Input 
                    type="password"
                    value={passwordForm.currentPassword} 
                    onChange={(e) => setPasswordForm(p => ({ ...p, currentPassword: e.target.value }))}
                    required
                    data-testid="password-current"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nouveau mot de passe</Label>
                  <Input 
                    type="password"
                    value={passwordForm.newPassword} 
                    onChange={(e) => setPasswordForm(p => ({ ...p, newPassword: e.target.value }))}
                    required
                    data-testid="password-new"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Confirmer le nouveau mot de passe</Label>
                  <Input 
                    type="password"
                    value={passwordForm.confirmPassword} 
                    onChange={(e) => setPasswordForm(p => ({ ...p, confirmPassword: e.target.value }))}
                    required
                    data-testid="password-confirm"
                  />
                </div>
                <Button type="submit" disabled={saving} className="btn-primary" data-testid="change-password">
                  <Lock className="w-4 h-4 mr-2" />
                  Changer le mot de passe
                </Button>
              </form>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default Account;
